import type { Instance } from "@gadgetinc/mobx-quick-tree";
import {
  BetaPill,
  Body,
  Button,
  ButtonKind,
  Caption,
  Card,
  Checkbox,
  Column,
  IconButton,
  Modal,
  paddingVertical,
  Pill,
  Radio,
  RadioGroup,
  Row,
  StaffPreviewPill,
  Subheading,
} from "@gadgetinc/widgets";
import DarkModeWindowImage from "@gadgetinc/widgets/assets/dark-mode-window.svg";
import LightModeWindowImage from "@gadgetinc/widgets/assets/light-mode-window.svg";
import SharpCornerImage from "@gadgetinc/widgets/assets/sharp-corner.svg";
import SoftCornerImage from "@gadgetinc/widgets/assets/soft-corner.svg";
import { BackArrowIcon } from "@gadgetinc/widgets/src/icons/BackArrowIcon";
import { EditIcon } from "@gadgetinc/widgets/src/icons/EditIcon";
import { GlobeIcon } from "@gadgetinc/widgets/src/icons/GlobeIcon";
import { ReactIcon } from "@gadgetinc/widgets/src/icons/ReactIcon";
import { ReactRouterIcon } from "@gadgetinc/widgets/src/icons/ReactRouterIcon";
import { RemixShopifyIcon } from "@gadgetinc/widgets/src/icons/RemixShopifyIcon";
import { ChatGPTCloneTemplateIcon } from "@gadgetinc/widgets/src/icons/appTemplates/ChatGPTCloneTemplateIcon";
import { BigCommerceIcon } from "@gadgetinc/widgets/src/icons/brands/BigCommerceIcon";
import { ShopifyIcon } from "@gadgetinc/widgets/src/icons/brands/ShopifyIcon";
import { useStyletron } from "baseui";
import { ParagraphSmall } from "baseui/typography";
import { capitalize, compact, get, orderBy } from "lodash";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { AppTemplateType, WebAppAuthType } from "state-trees/src/AppTemplateType";
import { ShopifyAppType } from "state-trees/src/assistant/AssistantMetadata";
import { FrontendFramework } from "state-trees/src/frontend";
import { CreatedOnMobile, getLastAppType } from "state-trees/src/tags";
import { AvailableV1Versions, AvailableVersions, latestConstraint } from "state-trees/src/versioning/FrameworkVersions";
import { useLocation } from "wouter";
import AppTemplates, {
  type AppTemplateDescriptor,
  appTemplatesAsArray,
  ChatGPTAuthOptions,
  SHOPIFY_GREEN,
  WebAppAuthOptions,
} from "../../client-state-trees/AppTemplates";
import { NewAppForm } from "../../client-state-trees/auth/NewAppForm";
import { InitialSurveySteps } from "../../client-state-trees/auth/Survey";
import { useFlag, useFlags } from "../../lib/flags";
import { useIsMobileOrSlimScreen } from "../../lib/useIsMobile";
import { BoundInput } from "../bound-controls/BoundInput";
import { BoundSelect } from "../bound-controls/BoundSelect";
import { BoundTextArea } from "../bound-controls/BoundTextArea";
import { CenteredSpin } from "../chrome/CenteredSpin";
import { SuccessIndicator } from "../chrome/SuccessIndicator";
import { AppTemplateTag } from "../edit/editor/AppTemplates";
import { DIRECTION, RadioCard, RadioCardGroup, DIRECTION as RadioGroupDirection, KIND as RadioGroupKind } from "../edit/shared/RadioCard";
import { LanguageIcon } from "../markdown/LanguageIndicator";
import { AuthTitle } from "./AuthTitle";
import { FormField } from "./FormField";
import { useCreateNewApp } from "./newApp.utls";
import { TechnicalSurvey } from "./survey/TechnicalSurvey";

interface User {
  name?: string | null;
  email: string;
  isStaff?: boolean;
}

const isForkAppTemplate = (
  appTemplateType: AppTemplateType | undefined,
  appTemplateDescriptor: AppTemplateDescriptor | null,
  forkFromAppId: string | undefined
) => {
  const appType = appTemplateType ?? AppTemplateType.Empty;

  const flatAppTemplates = Object.values(AppTemplates[appType]).flat();

  if (appTemplateDescriptor) {
    return flatAppTemplates.find((templateType) => templateType.title === appTemplateDescriptor?.title);
  } else if (forkFromAppId) {
    return appTemplatesAsArray.find((templateType) => "applicationId" in templateType && templateType.applicationId === forkFromAppId);
  } else {
    return flatAppTemplates.find((appTemplateType) => appTemplateType.default);
  }
};

const useSelectedAppTemplate = (form: Instance<typeof NewAppForm>) => {
  return useMemo(
    () => isForkAppTemplate(form.appTemplateType, form.appTemplateDescriptor, form.forkFromAppId),
    [form.appTemplateDescriptor, form.forkFromAppId, form.appTemplateType]
  );
};

const OnlyV1AppTypes = [AppTemplateType.BigCommerceApp];
const availableFrameworkVersionsForAppType = (appType?: AppTemplateType) => {
  if (appType && OnlyV1AppTypes.includes(appType)) {
    return AvailableV1Versions;
  }

  return AvailableVersions; // All framework versions available
};

const FrontendFrameworkReadOnlyItem = observer((props: { frontendFramework: FrontendFramework }) => {
  const [_css, $theme] = useStyletron();

  const icon = useMemo(() => {
    return props.frontendFramework === "react" ? (
      <ReactIcon color="#545454" />
    ) : props.frontendFramework === "react-router" ? (
      <ReactRouterIcon color="#545454" />
    ) : (
      <RemixShopifyIcon viewBoxSize={16} color={SHOPIFY_GREEN} />
    );
  }, [props.frontendFramework]);

  const name = useMemo(() => {
    return props.frontendFramework === "react"
      ? "React Router v7 declarative mode"
      : props.frontendFramework === "react-router"
      ? "React Router v7 framework mode"
      : "Remix";
  }, [props.frontendFramework]);

  return (
    <Row $align="space-between" $style={{ width: "100%" }}>
      <Row $gap={$theme.sizing.scale300}>
        {icon}
        <Body>{name}</Body>
      </Row>
    </Row>
  );
});

const SelectAppTemplateType = observer((props: { form: Instance<typeof NewAppForm>; disabled: boolean }) => {
  const [_css, $theme] = useStyletron();
  const flags = useFlags();
  const { form } = props;
  const [isEditing, setIsEditing] = useState(false);

  const appType = form.appTemplateType ?? AppTemplateType.Empty;
  const selectedAppTemplate = useSelectedAppTemplate(form);

  const appTemplateOptions = useMemo(() => {
    return compact(
      Object.entries(AppTemplates[appType]["Base"]).map(([_key, value]) => {
        if (value.requiredFlags && !value.requiredFlags.every((flag) => get(flags, flag))) {
          return null;
        }

        return {
          id: value.title,
          descriptor: value,
          label: (
            <Row $gap={$theme.sizing.scale200}>
              <Body>{value.title}</Body>
            </Row>
          ),
        };
      })
    );
  }, [appType, $theme.sizing.scale200, flags]);

  const canEdit = appTemplateOptions.length > 1;

  useEffect(() => {
    if (!form.appTemplateDescriptor && selectedAppTemplate && !form.forkFromAppId) {
      form.setAppTemplate(selectedAppTemplate);
    }
  }, [form.appTemplateDescriptor, selectedAppTemplate, form.forkFromAppId]);

  if (!isEditing) {
    return (
      <Row $align="space-between" $style={{ width: "100%" }}>
        <Row $gap={$theme.sizing.scale300}>
          {selectedAppTemplate?.icon}
          <Body>{selectedAppTemplate?.title}</Body>
        </Row>
        {canEdit && (
          <IconButton label="Edit" onClick={() => setIsEditing(true)} dataTestId="select-frontend-framework" disabled={form.submitting}>
            <EditIcon />
          </IconButton>
        )}
      </Row>
    );
  }

  return (
    <Column $gap={$theme.sizing.scale300}>
      <RadioGroup
        value={
          selectedAppTemplate ? appTemplateOptions.findIndex((option) => option.descriptor === selectedAppTemplate).toString() : undefined
        }
        onChange={(event) => {
          const descriptor = appTemplateOptions[parseInt(event.currentTarget.value)].descriptor;
          form.setAppTemplate(descriptor);
        }}
        disabled={form.submitting}
      >
        {appTemplateOptions.map((option, index) => (
          <Radio
            key={index}
            value={index.toString()}
            overrides={{
              Root: {
                style: {
                  marginTop: index === 0 ? undefined : $theme.sizing.scale500,
                },
              },
            }}
            dataTestId={`select-frontend-framework-${option.descriptor.frontendFramework}`}
          >
            {option.label}
          </Radio>
        ))}
      </RadioGroup>
    </Column>
  );
});

const languageValueToLabel = (language: string) => {
  return language === "javascript" ? "JavaScript" : "TypeScript";
};

const SelectLanguage = observer((props: { form: Instance<typeof NewAppForm> }) => {
  const { form } = props;
  const [_, $theme] = useStyletron();
  const [isEditing, setIsEditing] = useState(false);
  const selectedAppTemplate = useSelectedAppTemplate(form);

  if (!isEditing) {
    return (
      <Row $align="space-between" $style={{ width: "100%" }}>
        <Row $gap={$theme.sizing.scale300}>
          <LanguageIcon language={form.language} />
          <Body>{languageValueToLabel(form.language)}</Body>
        </Row>
        {selectedAppTemplate?.allowedLanguages?.length && selectedAppTemplate?.allowedLanguages?.length > 1 && (
          <IconButton label="Edit" onClick={() => setIsEditing(true)} dataTestId="select-language" disabled={form.submitting}>
            <EditIcon />
          </IconButton>
        )}
      </Row>
    );
  }

  return (
    <Column $gap={$theme.sizing.scale300}>
      <RadioGroup
        value={form.language}
        onChange={(event) => {
          form.setLanguage(event.currentTarget.value.toLowerCase() as "typescript" | "javascript");
        }}
        disabled={form.submitting}
      >
        {orderBy(selectedAppTemplate?.allowedLanguages.slice(), [(lang) => lang === "typescript"], ["desc"]).map((language, index) => (
          <Radio
            key={index}
            value={language}
            overrides={{
              Root: {
                style: {
                  marginTop: index === 0 ? undefined : $theme.sizing.scale500,
                },
              },
            }}
            dataTestId={`select-language-${language}`}
          >
            <Row $gap={$theme.sizing.scale300}>
              <LanguageIcon language={language} />
              <Body>{languageValueToLabel(language)}</Body>
            </Row>
          </Radio>
        ))}
      </RadioGroup>
    </Column>
  );
});

const AppTemplateIcon = (props: { icon: ReactNode; backgroundColor: string }) => {
  const [_, $theme] = useStyletron();

  return (
    <div
      style={{
        border: `1px solid ${$theme.colors.borderAlt}`,
        borderRadius: $theme.borders.radius300,
        width: "26px",
        height: "26px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: props.backgroundColor,
      }}
    >
      {props.icon}
    </div>
  );
};

const ForkedFromTemplateCard = observer((props: { selectedAppTemplate: AppTemplateDescriptor }) => {
  const { selectedAppTemplate } = props;
  const [_, $theme] = useStyletron();

  return selectedAppTemplate ? (
    <div
      style={{
        padding: $theme.sizing.scale500,
        borderRadius: $theme.borders.radius300,
        backgroundColor: $theme.colors.alpha50,
      }}
    >
      <Column $gap={$theme.sizing.scale500}>
        <Column $gap={$theme.sizing.scale200}>
          <Subheading $style={{ fontWeight: 600 }}>{selectedAppTemplate.title}</Subheading>
          <ParagraphSmall $style={{ color: $theme.colors.primary600 }}>{selectedAppTemplate.description}</ParagraphSmall>
        </Column>
        <Row>
          {selectedAppTemplate.tags && (
            <Row $gap={$theme.sizing.scale300} $style={{ flexWrap: "wrap" }}>
              {selectedAppTemplate.tags.map((tag) => (
                <AppTemplateTag key={tag} tag={tag} />
              ))}
            </Row>
          )}
        </Row>
      </Column>
    </div>
  ) : null;
});

type AppTemplates = {
  value: AppTemplateType;
  key: string;
  title: ReactNode;
  description: ReactNode;
  icon: ReactNode;
};

export const useNewAppTemplates = (appTemplateTypes?: AppTemplates[]) => {
  const { isMobile } = useIsMobileOrSlimScreen();
  const hasChatGPTAppTemplate = useFlag(["features", "chatGPTAppTemplate"]);
  return useMemo(
    () =>
      appTemplateTypes ??
      compact([
        {
          value: AppTemplateType.ShopifyApp,
          key: "shopify-app",
          title: "Shopify",
          description: "OAuth & data sync",
          icon: <AppTemplateIcon icon={<ShopifyIcon />} backgroundColor="#F8FFF0" />,
        },
        hasChatGPTAppTemplate
          ? {
              value: AppTemplateType.ChatGPTApp,
              key: "chatgpt-app",
              title: "ChatGPT",
              description: "ChatGPT app",
              icon: (
                <AppTemplateIcon
                  icon={<ChatGPTCloneTemplateIcon style={{ width: "24px", height: "24px" }} color="#000" backgroundColor="transparent" />}
                  backgroundColor="#FEF9FF"
                />
              ),
            }
          : undefined,
        isMobile
          ? undefined
          : {
              value: AppTemplateType.BigCommerceApp,
              key: "bigcommerce-app",
              title: "BigCommerce",
              description: "OAuth & webhook registration",
              icon: <AppTemplateIcon icon={<BigCommerceIcon style={{ width: "20px", height: "20px" }} />} backgroundColor="#FAF9FF" />,
            },
        {
          value: AppTemplateType.WebAppWithAuth,
          key: "web-app",
          title: "Web",
          description: "Built-in user auth",
          icon: <AppTemplateIcon icon={<GlobeIcon style={{ width: "20px", height: "20px" }} />} backgroundColor="#F2F2FF" />,
        },
      ]),
    [appTemplateTypes, hasChatGPTAppTemplate, isMobile]
  );
};

const AppTemplateRadioCard = observer((props: { template: AppTemplates }) => {
  const { template } = props;
  const [_, $theme] = useStyletron();

  return (
    <RadioCard
      value={template.value}
      dataTestId={`select-app-template-${template.value}`}
      key={template.key}
      direction={DIRECTION.Row}
      hideRadio
      style={{
        ...paddingVertical($theme.sizing.scale300),
        paddingLeft: $theme.sizing.scale300,
        paddingRight: $theme.sizing.scale500,
      }}
    >
      <Column $style={{ gap: $theme.sizing.scale300, whiteSpace: "nowrap" }}>
        {template.icon}
        <div key={template.key}>{template.title}</div>
      </Column>
    </RadioCard>
  );
});

export const AppTemplateRadioGroup = observer(
  (props: { value: AppTemplateType | undefined; onChange: (value: AppTemplateType) => void; appTemplateTypes?: AppTemplates[] }) => {
    const { value, onChange } = props;
    const appTemplateTypes = useNewAppTemplates(props.appTemplateTypes);
    const [_, $theme] = useStyletron();

    return (
      <RadioCardGroup
        kind={RadioGroupKind.Stacked}
        style={{ marginBottom: "0px" }}
        direction={RadioGroupDirection.Row}
        value={value}
        onChange={(value: string) => {
          onChange(value as AppTemplateType);
        }}
      >
        {[
          <div
            data-testid="select-auth-system"
            key="auth-system"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
              gap: $theme.sizing.scale300,
              maxWidth: "100%",
            }}
          >
            {appTemplateTypes.map((template) => (
              <AppTemplateRadioCard key={template.key} template={template} />
            ))}
          </div>,
        ]}
      </RadioCardGroup>
    );
  }
);

export const ShopifyAppTypeRadioGroup = observer(
  (props: { value: ShopifyAppType | undefined; onChange: (value: ShopifyAppType) => void }) => {
    const { value, onChange } = props;
    const [_, $theme] = useStyletron();

    return (
      <Column $gap={$theme.sizing.scale500}>
        <Body
          $style={{
            fontWeight: 600,
          }}
        >
          App type
        </Body>
        <RadioGroup
          value={value}
          onChange={(event) => {
            onChange(event.currentTarget.value as ShopifyAppType);
          }}
        >
          <Radio
            value={ShopifyAppType.Custom}
            overrides={{
              Description: {
                style: {
                  fontSize: "12px",
                  marginLeft: "24px",
                  color: $theme.colors.primary600,
                  maxWidth: undefined,
                },
              },
            }}
            description="For apps that are installed by one brand"
            dataTestId="shopify-app-type-custom"
          >
            Custom app
          </Radio>
          <Radio
            value={ShopifyAppType.Public}
            overrides={{
              Root: {
                style: {
                  marginTop: $theme.sizing.scale500,
                },
              },
              Description: {
                style: {
                  fontSize: "12px",
                  marginLeft: "24px",
                  color: $theme.colors.primary600,
                  maxWidth: undefined,
                },
              },
            }}
            description="For apps that are installed by many different brands"
            dataTestId="shopify-app-type-public"
          >
            Public app
          </Radio>
        </RadioGroup>
      </Column>
    );
  }
);

export const WebAppAuthTypeRadioGroup = observer(
  (props: { value: WebAppAuthType | undefined; onChange: (value: WebAppAuthType) => void; appTemplateType?: AppTemplateType | null }) => {
    const { value, onChange, appTemplateType } = props;
    const [_, $theme] = useStyletron();

    // Use ChatGPTAuthOptions for ChatGPT apps, otherwise use WebAppAuthOptions
    const authOptions = appTemplateType === AppTemplateType.ChatGPTApp ? ChatGPTAuthOptions : WebAppAuthOptions;

    if (Object.keys(authOptions).length === 2) {
      const handleCheckboxChange = (checked: boolean) => {
        onChange(checked ? WebAppAuthType.EmailPassword : WebAppAuthType.NoAuth);
      };

      const isAuthEnabled = value === WebAppAuthType.EmailPassword;
      const authOption = authOptions[WebAppAuthType.EmailPassword];

      return (
        <Column $gap={$theme.sizing.scale500}>
          <Body $style={{ fontWeight: 600 }}>Will your app need to authenticate users?</Body>

          <Column>
            <Checkbox
              dataTestId="web-app-enable-auth"
              checked={isAuthEnabled}
              onChange={(event) => handleCheckboxChange(event.currentTarget.checked)}
            >
              Yes, enable auth
            </Checkbox>
            <Caption $style={{ marginLeft: $theme.sizing.scale850 }}>Includes email/password (with recovery) and Google SSO.</Caption>
          </Column>
        </Column>
      );
    }

    return (
      <Column $gap={$theme.sizing.scale500}>
        <Body
          $style={{
            fontWeight: 600,
          }}
        >
          Auth method
        </Body>
        <RadioGroup
          value={value}
          onChange={(event) => {
            onChange(event.currentTarget.value as WebAppAuthType);
          }}
        >
          {Object.entries(authOptions).map(([key, option], index) => (
            <Radio
              key={key}
              value={key}
              data-testid={`select-web-app-auth-type-${key}`}
              overrides={{
                Root:
                  index === 0
                    ? undefined
                    : {
                        style: {
                          marginTop: $theme.sizing.scale500,
                        },
                      },
                Description: {
                  style: {
                    fontSize: "12px",
                    marginLeft: "24px",
                    color: $theme.colors.primary600,
                    maxWidth: undefined,
                  },
                },
              }}
              description={option.description}
              dataTestId={`select-web-app-auth-type-${key}`}
            >
              {option.title}
            </Radio>
          ))}
        </RadioGroup>
      </Column>
    );
  }
);

const SelectAppType = observer((props: { form: Instance<typeof NewAppForm>; newApp: boolean }) => {
  const { form, newApp } = props;
  const [_, $theme] = useStyletron();
  const selectedAppTemplate = useSelectedAppTemplate(form);
  const flags = useFlags();

  const handleSubmit = useCallback(() => {
    form.moveToNextStep();
  }, [form, newApp]);

  const forkFromTemplate = form.forkFromAppId && selectedAppTemplate;
  const isChatGPTApp = form.appTemplateType === AppTemplateType.ChatGPTApp;
  return (
    <form
      style={{ width: "100%", display: "flex" }}
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSubmit();
        }
      }}
    >
      <Column $align="space-between" $gap={$theme.sizing.scale800} $style={{ width: "100%" }}>
        {!newApp && selectedAppTemplate && (
          <Column>
            <ForkedFromTemplateCard selectedAppTemplate={selectedAppTemplate} />
          </Column>
        )}
        {newApp && (
          <AppTemplateRadioGroup
            value={form.appTemplateType}
            onChange={(value) => {
              form.setAppType(value);
              form.setAppTemplateFromAppTemplateType(value);
            }}
          />
        )}

        {!isChatGPTApp && (!forkFromTemplate || (forkFromTemplate && flags.features.customizeTemplate)) && (
          <Column>
            <FormField
              label={
                forkFromTemplate ? (
                  <Row $gap={$theme.sizing.scale200}>
                    <span>How would you like to customize this template?</span>
                    <Caption>(optional)</Caption>
                  </Row>
                ) : (
                  <Row $gap={$theme.sizing.scale200}>
                    <span>What are you building?</span>
                    <Caption>(optional)</Caption>
                  </Row>
                )
              }
            >
              <Row>
                <BoundTextArea
                  dataTestId="app-description"
                  autoFocus
                  spellcheck={false}
                  node={form.app}
                  property="description"
                  overrides={{ Input: { style: { height: "104px" } } }}
                />
              </Row>
            </FormField>
          </Column>
        )}

        {newApp && form.appTemplateType === AppTemplateType.ShopifyApp && (
          <ShopifyAppTypeRadioGroup
            value={form.app.shopifyAppType}
            onChange={(value) => {
              form.app.setShopifyAppType(value);
            }}
          />
        )}

        {newApp && (form.appTemplateType === AppTemplateType.WebAppWithAuth || form.appTemplateType === AppTemplateType.ChatGPTApp) && (
          <WebAppAuthTypeRadioGroup
            value={form.webAppAuthType}
            onChange={(value) => {
              form.setWebAppAuthType(value);
            }}
            appTemplateType={form.appTemplateType}
          />
        )}

        <Row $style={{ justifyContent: "flex-end", gap: $theme.sizing.scale400 }}>
          <Button data-testid="go-to-name-app" kind={ButtonKind.primary} disabled={!form.appTemplateType} type="submit">
            Continue
          </Button>
        </Row>
      </Column>
    </form>
  );
});

const NameApp = observer((props: { form: Instance<typeof NewAppForm>; renderDomains: string[]; newApp: boolean; currentUser?: User }) => {
  const { form } = props;
  const [css, $theme] = useStyletron();
  const { create, validate } = useCreateNewApp();
  const showFrameworkVersionSelect = useFlag(["internal", "selectFrameworkVersionOnCreate"]) && !form.forkFromAppId;

  const selectedAppTemplate = useSelectedAppTemplate(form);
  const isStaff = props.currentUser?.isStaff;

  useEffect(() => {
    if (form.appTemplateType) {
      const disposer = reaction(
        () => form.dataBlob(),
        () => void form.validate(validate),
        { delay: 200 }
      );
      return disposer;
    }
  }, [form, validate, form.appTemplateType]);

  const getPlaceHolderText = (appType: AppTemplateType | undefined) => {
    switch (appType) {
      case AppTemplateType.ShopifyApp:
        return "my-shopify-app";
      case AppTemplateType.WebAppWithAI:
        return "my-ai-app";
      case AppTemplateType.WebAppWithAuth:
        return "my-web-app";
      case AppTemplateType.BigCommerceApp:
        return "my-bigcommerce-app";
      default:
        return "my-app";
    }
  };

  const showTypescriptSupportCheckbox = !!selectedAppTemplate;

  return (
    <form
      className={css({ width: "100%" })}
      onSubmit={(event) => {
        event.preventDefault();
        if (form.submittable) {
          void form.submit(create, validate);
        }
      }}
    >
      <Column
        $style={{
          gap: $theme.sizing.scale600,
          width: "600px",
          maxWidth: "100%",
        }}
        $align="space-between"
      >
        <Column>
          <FormField label="App name" error={capitalize(form.errors.get("domain"))}>
            <Row
              $style={{
                width: "294px",
              }}
            >
              <BoundInput
                dataTestId="application-domain"
                autoFocus
                node={form.app}
                property="subdomain"
                disabled={form.submitting}
                placeholder={getPlaceHolderText(form.appTemplateType)}
                maxLength={54}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && form.submittable) {
                    void form.submit(create, validate);
                  }
                }}
              />
            </Row>
          </FormField>
        </Column>

        {(props.newApp || selectedAppTemplate) && (
          <Card
            $style={{
              padding: "0px",
            }}
          >
            {form.appTemplateType !== AppTemplateType.BigCommerceApp && form.appTemplateType !== AppTemplateType.ChatGPTApp && (
              <Row
                $gap={$theme.sizing.scale500}
                $style={{
                  alignItems: "flex-start",
                  borderBottom: form.forkFromAppId ? undefined : `1px solid ${$theme.colors.alpha200}`,
                  padding: $theme.sizing.scale500,
                }}
              >
                <Body
                  $style={{
                    color: $theme.colors.primary600,
                    width: "100px",
                  }}
                >
                  Frontend
                </Body>
                {props.newApp ? (
                  <SelectAppTemplateType form={form} disabled={!props.newApp} />
                ) : selectedAppTemplate?.frontendFramework ? (
                  <FrontendFrameworkReadOnlyItem frontendFramework={selectedAppTemplate.frontendFramework} />
                ) : null}
              </Row>
            )}

            {showFrameworkVersionSelect && (
              <Row
                $gap={$theme.sizing.scale500}
                $style={{
                  alignItems: "flex-start",
                  borderBottom: `1px solid ${$theme.colors.alpha200}`,
                  padding: $theme.sizing.scale500,
                }}
              >
                <Column>
                  <Body
                    $style={{
                      color: $theme.colors.primary600,
                      width: "150px",
                    }}
                  >
                    Framework version
                  </Body>
                  <StaffPreviewPill />
                </Column>
                <Row $style={{ width: "100%" }}>
                  <BoundSelect
                    node={form.app}
                    property="frameworkVersion"
                    disabled={form.submitting}
                    escapeClearsValue={false}
                    clearable={false}
                    searchable={false}
                    options={availableFrameworkVersionsForAppType(form.appTemplateType).map((version) => ({
                      id: version.constraint,
                      label: (
                        <>
                          <>
                            <Row $gap={$theme.sizing.scale200}>
                              <b>{version.name}</b>
                              {version.beta && <BetaPill>beta</BetaPill>}
                              {!version.released && !version.beta && <Pill $color="yellow">unreleased</Pill>}
                              {version.constraint == latestConstraint() && <Pill $color="green">latest</Pill>}
                            </Row>
                            {version.description}
                          </>
                        </>
                      ),
                    }))}
                  />
                </Row>
              </Row>
            )}

            {showTypescriptSupportCheckbox && (
              <Row
                $gap={$theme.sizing.scale500}
                $style={{
                  alignItems: "flex-start",
                  padding: $theme.sizing.scale500,
                }}
              >
                <Body
                  $style={{
                    color: $theme.colors.primary600,
                    width: "100px",
                  }}
                >
                  Language
                </Body>
                <SelectLanguage form={form} />
              </Row>
            )}
          </Card>
        )}

        <Row $style={{ justifyContent: "flex-end", gap: "10px", alignItems: "center" }}>
          <Button
            data-testid="create-app"
            kind={form.successful ? ButtonKind.secondary : ButtonKind.primary}
            disabled={!form.submittable || form.submitting || form.successful}
            isLoading={form.submitting}
          >
            {form.successful ? <SuccessIndicator $size={$theme.sizing.scale800} /> : "Confirm"}
          </Button>
        </Row>
      </Column>
    </form>
  );
});

export type NewAppProps = {
  renderDomains: string[];
  hasShopifyTag: boolean;
  showSurvey: boolean;
  isNewUser: boolean;
  aiEnabled?: boolean;
  defaultFrameworkVersion: string;
  currentUser?: User;
  userId?: string;
  userTags?: string[];
  userEmailHash?: string;
  isStaff?: boolean;
  forkSourceApp?: { name: string; domain: string; id: string; appType: AppTemplateType };
  includedApplications?: number | null;
  numberOfIncludedAppsRemaining?: number | null;
  initialAppType?: AppTemplateType;
};

const FormContents = observer((props: { form: Instance<typeof NewAppForm>; newApp: NewAppProps }) => {
  const { form, newApp } = props;

  if (form.step === 0) {
    return <SelectAppType form={props.form} newApp={!newApp.forkSourceApp} />;
  }

  if (form.step === 1) {
    return (
      <NameApp form={form} renderDomains={newApp.renderDomains} newApp={!newApp.forkSourceApp} currentUser={props.newApp.currentUser} />
    );
  }

  return null;
});

export const NewAppModalContents = observer((props: NewAppProps) => {
  const [css, $theme] = useStyletron();
  const [location, setLocation] = useLocation();
  const flags = useFlags();

  useEffect(() => {
    // Preload images to avoid flickering
    const images = [LightModeWindowImage, DarkModeWindowImage, SoftCornerImage, SharpCornerImage];
    for (const image of images) {
      const img = new Image();
      img.src = image;
    }
  }, []);

  useEffect(() => {
    if (location.endsWith("?reset=true")) {
      setLocation(location.replace("?reset=true", ""));
    }
  }, [location, setLocation]);

  const form = useMemo(() => {
    let selectedAppType: AppTemplateType | undefined = undefined;

    if (props.forkSourceApp?.appType) {
      selectedAppType = props.forkSourceApp.appType;
    } else if (props.initialAppType) {
      selectedAppType = props.initialAppType;
    } else if (props.userTags) {
      const maybeAppTemplate = getLastAppType(props.userTags);
      if (maybeAppTemplate) {
        selectedAppType = maybeAppTemplate === AppTemplateType.WebAppWithAuth ? AppTemplateType.WebAppWithAuth : AppTemplateType.ShopifyApp;
      }
    }

    const forkingAppTemplate = props.forkSourceApp
      ? isForkAppTemplate(props.forkSourceApp.appType, null, props.forkSourceApp.id)
      : undefined;

    const appTemplateType = selectedAppType ?? AppTemplateType.ShopifyApp;
    const newAppSnapshot = {
      forkFromAppId: props.forkSourceApp?.id,
      appTemplateType,
      app: { frameworkVersion: props.forkSourceApp?.id ? null : props.defaultFrameworkVersion },
      step: props.forkSourceApp?.id && !forkingAppTemplate ? 1 : 0,
      lowestAccessibleStep: props.forkSourceApp?.id && !forkingAppTemplate ? 1 : 0,
      survey: props.userId
        ? {
            userId: String(props.userId),
            steps: InitialSurveySteps,
            stepIndex: 0,
          }
        : undefined,
    };

    const form = NewAppForm.create(newAppSnapshot);
    if (!props.forkSourceApp) {
      form.setAppTemplateFromAppTemplateType(appTemplateType);
    }
    return form;
  }, [props.userId, props.userTags, props.defaultFrameworkVersion, props.forkSourceApp, props.initialAppType]);

  const { isMobile } = useIsMobileOrSlimScreen();
  useEffect(() => {
    if (isMobile) {
      form.addTag(CreatedOnMobile);
    }
  }, [isMobile]);

  const title = useMemo(() => {
    if (props.forkSourceApp) {
      return `Fork ${props.forkSourceApp.name.replaceAll("-", " ")}`;
    }

    if (form.step === 0) {
      return "Create new app";
    }

    switch (form.appTemplateType) {
      case AppTemplateType.ShopifyApp:
        return "Create a new Shopify app";
      case AppTemplateType.WebAppWithAI:
        return "Create a new AI app";
      case AppTemplateType.WebAppWithAuth:
        return "Create a new web app";
      case AppTemplateType.BigCommerceApp:
        return "Create a new BigCommerce app";
      case AppTemplateType.ChatGPTApp:
        return "Create a new ChatGPT app";
      default:
        throw new Error(`Unknown app template type: ${form.appTemplateType}`);
    }
  }, [props.forkSourceApp, form.step, form.appTemplateType]);

  const showSurvey = props.showSurvey && form.survey?.step;

  if (showSurvey) {
    if (typeof window !== "undefined") {
      switch (form.survey?.step?.key) {
        case "technicalLevel":
          return <TechnicalSurvey step={form.survey.step} />;
      }
    } else {
      return (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "white",
          }}
        />
      );
    }
  }

  return (
    <>
      <Helmet>
        <title>Create an app</title>
      </Helmet>
      <Column
        $as="article"
        $style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div
          className={css({
            display: !form.suspended ? "none" : undefined,
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            backgroundColor: $theme.colors.backgroundPrimary,
            zIndex: 100,
          })}
        >
          <CenteredSpin />
        </div>

        <Column $gap={$theme.sizing.scale800} $style={{ width: "100%" }}>
          <Row $align="space-between" $style={{ flex: 1 }}>
            <Row $gap={$theme.sizing.scale300}>
              {flags.features.customizeTemplate &&
              props.forkSourceApp &&
              isForkAppTemplate(props.forkSourceApp.appType, null, props.forkSourceApp.id) &&
              form.step === 0 ? (
                <IconButton
                  label="Back"
                  onClick={() => {
                    setLocation("/auth/team/templates");
                  }}
                >
                  <BackArrowIcon />
                </IconButton>
              ) : (
                form.step > 0 &&
                form.step > form.lowestAccessibleStep && (
                  <IconButton
                    label="Back"
                    onClick={() => {
                      form.moveToPreviousStep();
                    }}
                  >
                    <BackArrowIcon />
                  </IconButton>
                )
              )}
              <AuthTitle>{title}</AuthTitle>
            </Row>
          </Row>
          <FormContents form={form} newApp={props} />
        </Column>
      </Column>
    </>
  );
});

const NewAppPage = (props: NewAppProps) => {
  const [_, setLocation] = useLocation();

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        setLocation("/auth/team");
      }}
      size="auto"
    >
      <NewAppModalContents {...props} />
    </Modal>
  );
};

export default NewAppPage;
